import { DetailViewModel } from "@frui.ts/datascreens";
import { bound } from "@frui.ts/helpers";
import { watchBusy } from "@frui.ts/screens";
import { ManualEntityValidator, validate } from "@frui.ts/validation";
import { attachAutomaticValidator } from "@frui.ts/validation/dist";
import * as Sentry from "@sentry/react";
import { Modules } from "config";
import VehicleChange, { VehicleChangeValidationRulesFactory } from "entities/vehicleChange";
import type { IReactionDisposer } from "mobx";
import { computed, observable, reaction } from "mobx";
import type ChargesRepository from "repositories/chargesRepository";
import type EnumService from "services/enumService";
import type EventBus from "services/eventBus";
import { vehiclePlatesChangeChanged } from "services/eventBus";
import type LocalizationService from "services/localizationService";
import type NotificationService from "services/notificationService";
import { NotificationType } from "services/notificationService";
import ChangeVehicleRegistrationRequest from "entities/changeVehicleRegistrationRequest";
import LicensePlate from "entities/licensePlate";
import type CountrySelectViewModel from "../../countrySelectViewModel";
import type SourceType from "entities/sourceType";
import type EnumRepository from "repositories/enumRepository";
import VehicleRegistrationChangePreviewResponse from "entities/vehicleRegistrationChangePreviewResponse";
import type VehicleRegistrationChangeChargeResponse from "entities/vehicleRegistrationChangeChargeResponse";
import type VehicleRegistrationChangeExemptionResponse from "entities/vehicleRegistrationChangeExemptionResponse";
import type VehicleRegisterSynchronizationStatus from "entities/vehicleRegisterSynchronizationStatus";
import type VehicleRegisterVehicleIdentificationResponse from "entities/vehicleRegisterVehicleIdentificationResponse";
import type PriceListService from "services/priceListService";
import type VehicleRegisterVehicleClassifiedFuelType from "entities/vehicleRegisterVehicleClassifiedFuelType";
import ChargeOrderPaymentDisplayType from "entities/chargeOrderPaymentDisplayType";
import VehicleRegistrationChangeRejectionReason from "entities/vehicleRegistrationChangeRejectionReason";
import { emptyValue } from "utils/emptyValue";
import { translateChargeTypeByPriceListItem } from "utils/chargeType";

export default class ChangeVehicleViewModel extends DetailViewModel<VehicleChange> {
  private unsubscriptions: IReactionDisposer[] = [];
  countrySelectViewModel: CountrySelectViewModel;
  protected manualValidator = new ManualEntityValidator<VehicleChange>(false);
  @observable searchResponse?: VehicleRegistrationChangePreviewResponse;

  constructor(
    private countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>,
    private repository: ChargesRepository,
    private localizationService: LocalizationService,
    private notificationService: NotificationService,
    private priceListService: PriceListService,
    private enumService: EnumService,
    protected enumRepository: EnumRepository,
    private eventBus: EventBus
  ) {
    super();
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get secondStep() {
    return !!this.searchResponse && !this.noChargesNoExemptionsWarning;
  }

  @computed
  get noChargesNoExemptionsWarning() {
    return !!(
      this.searchPreview &&
      (this.searchPreview.rejectionReason === VehicleRegistrationChangeRejectionReason.NotItemsForChangeFound ||
        (this.searchPreview?.charges.length === 0 && this.searchPreview?.exemptions.length === 0))
    );
  }

  @computed
  get noWarnings() {
    return this.searchPreview && this.searchPreview.warningReasons.length === 0;
  }

  @computed
  get rejectedReason() {
    return this.searchPreview?.rejectionReason;
  }

  @computed
  get rejectedReasonByPassing() {
    if (!this.searchPreview?.rejectionReason) {
      return undefined;
    }

    const reason = this.searchPreview?.rejectionReason;

    if (reason === VehicleRegistrationChangeRejectionReason.WarningBypassingForbidden) {
      return this.translate("warningBypassingForbidden");
    }
    return this.translate("noChargesNoExemptionsWarning");
  }

  @computed
  get canBeSubmitted() {
    return this.searchPreview?.canBeSubmitted;
  }

  @computed
  get isSameVehicle() {
    return (
      this.item.oldCountryId === this.item.newCountryId &&
      this.item.oldLicensePlate === this.item.newLicensePlate &&
      !!this.item.oldLicensePlate &&
      !!this.item.oldCountryId
    );
  }

  @computed
  get charges() {
    return this.searchPreview?.charges ?? [];
  }

  @computed
  get exemptions() {
    return this.searchPreview?.exemptions ?? [];
  }

  @computed
  get searchPreview() {
    return this.searchResponse ?? null;
  }

  @computed
  get vehicleRegisterVehicle() {
    return this.searchResponse?.newVehicleRegisterVehicle ?? null;
  }

  async onInitialize(): Promise<void> {
    await super.onInitialize();

    this.unsubscriptions.push(
      reaction(() => [this.item.oldCountryId, this.item.oldLicensePlate], this.onBackClick),
      reaction(
        () => [this.item.oldCountryId, this.item.oldLicensePlate, this.item.newCountryId, this.item.newLicensePlate],
        this.removeSameVehicleErrors
      )
    );
  }

  setItem(item: VehicleChange) {
    const czechCountryId = this.enumService.values("countries").find(country => country.code === "CZ")?.id;
    if (czechCountryId && !item.oldCountryId) {
      item.oldCountryId = `${czechCountryId}`;
    }
    super.setItem(item);
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  @bound
  protected removeSameVehicleErrors() {
    this.manualValidator.removeError("newLicensePlate");
    this.manualValidator.removeError("newCountryId");
  }

  @bound
  protected sameVehicleCheck() {
    this.removeSameVehicleErrors();

    if (this.isSameVehicle) {
      const errorMessage = this.translate("sameVehicleError");
      this.manualValidator.addError("newLicensePlate", errorMessage);
      this.manualValidator.addError("newCountryId", errorMessage);
    }
  }

  @bound
  translateExemptionReasonId(exemptionReasonId: string) {
    const matchingExemptionReason = this.getExemptionReason(exemptionReasonId);
    return matchingExemptionReason?.description ?? emptyValue;
  }

  @bound
  getExemptionReason(exemptionReasonId: string) {
    const exemptionReasons = this.enumService.values("notifiable_exemption_reasons");

    return exemptionReasons.find(x => x.id === exemptionReasonId);
  }

  @bound
  onSubmit() {
    this.sameVehicleCheck();
    if (this.secondStep) {
      this.confirmAndSubmit();
    } else {
      this.searchChargesAndExemptions();
    }
  }

  @bound
  @watchBusy
  private async searchChargesAndExemptions() {
    if (!validate(this.item)) {
      return;
    }
    try {
      const searchRequest = new ChangeVehicleRegistrationRequest();
      searchRequest.licensePlate = this.item.newLicensePlate;
      searchRequest.countryId = this.item.newCountryId;
      searchRequest.submissionDate = this.item.submissionDate;
      searchRequest.officialRecordId = this.item.officialRecordId;

      this.searchResponse = await this.repository.vehicleRegistrationChangeSearch(
        searchRequest,
        this.item.oldCountryId,
        this.item.oldLicensePlate
      );
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  @bound
  @watchBusy
  private async confirmAndSubmit() {
    const chargesCount = this.charges.length;
    const exemptionsCount = this.exemptions.length;

    if (chargesCount || exemptionsCount) {
      try {
        const { oldCountryId, oldLicensePlate } = this.item;
        const payload = new ChangeVehicleRegistrationRequest();
        payload.licensePlate = this.item.newLicensePlate;
        payload.countryId = this.item.newCountryId;
        payload.submissionDate = this.item.submissionDate;
        payload.officialRecordId = this.item.officialRecordId;

        await this.repository.changeAllPlates(new LicensePlate(oldLicensePlate, oldCountryId), payload);
        this.eventBus.publish(
          vehiclePlatesChangeChanged({ entity: this.item, exemptions: this.exemptions, charges: this.charges })
        );

        let notificationKey = "changeSucceed";
        if (chargesCount > 0 && exemptionsCount === 0) {
          notificationKey = "chargesChangeSucceed";
        } else if (exemptionsCount > 0 && chargesCount === 0) {
          notificationKey = "exemptionsChangeSucceed";
        }

        this.notificationService.add({
          title: this.translate(notificationKey, chargesCount)
            .replace("%chargesCount%", chargesCount.toString())
            .replace("%exemptionsCount%", exemptionsCount.toString()),
          type: NotificationType.SUCCESS,
        });
        this.requestClose();
      } catch (e) {
        Sentry.captureException(e);
      }
    }
  }

  @bound
  onBackClick() {
    this.searchResponse = undefined;
  }

  protected loadDetail() {
    return attachAutomaticValidator(new VehicleChange(), VehicleChangeValidationRulesFactory(this.manualValidator));
  }

  translatePurchased(chargeResponse: VehicleRegistrationChangeChargeResponse) {
    let purchased = this.translateSourceType(chargeResponse.sourceType);

    if (
      chargeResponse.chargeOrderPaymentDisplayType &&
      chargeResponse.chargeOrderPaymentDisplayType !== ChargeOrderPaymentDisplayType.Terminal
    ) {
      purchased += ` - ${this.translatePaymentType(chargeResponse.chargeOrderPaymentDisplayType)}`;
    }

    return purchased;
  }

  translateSourceType(sourceType: SourceType) {
    return this.localizationService.translateEnum("sourceType", sourceType);
  }

  translatePaymentType(paymentType: ChargeOrderPaymentDisplayType) {
    const lowerCasePaymentType = paymentType?.toLowerCase();

    if (lowerCasePaymentType?.startsWith("card")) {
      return this.translate("payGate");
    }
    return this.translate("transfer");
  }

  translateClassifiedFuelType(classifiedFuelType: VehicleRegisterVehicleClassifiedFuelType) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleClassifiedFuelType", classifiedFuelType);
  }

  translateChargeType(priceListId: string) {
    return translateChargeTypeByPriceListItem(this.priceListService, this.enumService, priceListId);
  }

  translateFuelType(priceListId: string) {
    const fuelTypeId = this.priceListService.chargePriceList.find(x => x.id === priceListId)?.fuelTypeId;
    if (!fuelTypeId) {
      return emptyValue;
    }
    return this.enumService.value("fuel_types", fuelTypeId)?.name ?? "";
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `vehicleChange.${code}`, size);
  }

  translateAttribute(attribute: keyof VehicleChange, size?: number) {
    return this.localizationService.translateAttribute("vehicleChange", attribute, size);
  }

  translatePreviewAttribute(attribute: keyof VehicleRegistrationChangePreviewResponse, size?: number) {
    return this.localizationService.translateAttribute("vehicleRegistrationPreview", attribute, size);
  }
  translateVehicleRegisterIdentification(attribute: keyof VehicleRegisterVehicleIdentificationResponse, size?: number) {
    return this.localizationService.translateAttribute("vehicleRegisterIdentification", attribute, size);
  }

  translateVehicleRegisterSynchronizationStatus(status: VehicleRegisterSynchronizationStatus) {
    return this.localizationService.translateEnum("vehicleRegisterSynchronizationStatus", status);
  }

  translateSubAttribute(model: "changeVehicleCharge", attribute: keyof VehicleRegistrationChangeChargeResponse): string;
  translateSubAttribute(model: "changeVehicleExemption", attribute: keyof VehicleRegistrationChangeExemptionResponse): string;
  translateSubAttribute(model: string, attribute: string): string {
    return this.localizationService.translateAttribute(model, attribute);
  }

  translateExemptionReason(exemptionReason: string) {
    return this.enumService.value("exemption_reasons", exemptionReason)?.description ?? emptyValue;
  }

  translatePaymentDisplayType(paymentType: ChargeOrderPaymentDisplayType) {
    return this.localizationService.translateEnum("paymentDisplayType", paymentType);
  }

  translateCountry(countryId: string) {
    return this.enumService.value("countries", countryId)?.code ?? "";
  }

  translateCountryName(countryId: string) {
    return this.enumService.value("countries", countryId)?.name ?? "";
  }

  formatCountry(countryId: string) {
    return `${this.translateCountry(countryId)} - ${this.translateCountryName(countryId)}`;
  }
}
